/**
 * Draw an arrow from (x1,y1) to (x2,y2)
 */
export function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  headLength = 20
) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLength * Math.cos(angle - Math.PI / 6),
    y2 - headLength * Math.sin(angle - Math.PI / 6)
  );
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headLength * Math.cos(angle + Math.PI / 6),
    y2 - headLength * Math.sin(angle + Math.PI / 6)
  );
  ctx.stroke();
}

/**
 * Apply Gaussian blur to a canvas region
 */
export function applyBlurToRegion(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  blurLevel: number
) {
  const normalizedX = width < 0 ? x + width : x;
  const normalizedY = height < 0 ? y + height : y;
  const normalizedWidth = Math.abs(width);
  const normalizedHeight = Math.abs(height);

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const clampedX = Math.max(0, Math.min(normalizedX, canvasWidth));
  const clampedY = Math.max(0, Math.min(normalizedY, canvasHeight));
  const clampedWidth = Math.min(normalizedWidth, canvasWidth - clampedX);
  const clampedHeight = Math.min(normalizedHeight, canvasHeight - clampedY);

  if (clampedWidth <= 0 || clampedHeight <= 0) return;

  const offscreenCanvas = document.createElement("canvas");
  offscreenCanvas.width = clampedWidth;
  offscreenCanvas.height = clampedHeight;
  const offscreenCtx = offscreenCanvas.getContext("2d");
  if (!offscreenCtx) return;

  offscreenCtx.drawImage(
    canvas,
    clampedX,
    clampedY,
    clampedWidth,
    clampedHeight,
    0,
    0,
    clampedWidth,
    clampedHeight
  );

  const blurCanvas = document.createElement("canvas");
  blurCanvas.width = clampedWidth;
  blurCanvas.height = clampedHeight;
  const blurCtx = blurCanvas.getContext("2d");
  if (!blurCtx) return;

  blurCtx.filter = `blur(${blurLevel}px)`;
  blurCtx.drawImage(offscreenCanvas, 0, 0);

  ctx.drawImage(
    blurCanvas,
    0,
    0,
    clampedWidth,
    clampedHeight,
    clampedX,
    clampedY,
    clampedWidth,
    clampedHeight
  );
}

/**
 * Apply pixelation (tiles) blur to a canvas region
 */
export function applyTilesBlurToRegion(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  pixelSize: number
) {
  const normalizedX = width < 0 ? x + width : x;
  const normalizedY = height < 0 ? y + height : y;
  const normalizedWidth = Math.abs(width);
  const normalizedHeight = Math.abs(height);

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const clampedX = Math.max(0, Math.min(normalizedX, canvasWidth));
  const clampedY = Math.max(0, Math.min(normalizedY, canvasHeight));
  const clampedWidth = Math.min(normalizedWidth, canvasWidth - clampedX);
  const clampedHeight = Math.min(normalizedHeight, canvasHeight - clampedY);

  if (clampedWidth <= 0 || clampedHeight <= 0) return;

  const smallWidth = Math.max(1, Math.ceil(clampedWidth / pixelSize));
  const smallHeight = Math.max(1, Math.ceil(clampedHeight / pixelSize));

  const offscreenCanvas = document.createElement("canvas");
  offscreenCanvas.width = smallWidth;
  offscreenCanvas.height = smallHeight;
  const offscreenCtx = offscreenCanvas.getContext("2d");
  if (!offscreenCtx) return;

  offscreenCtx.drawImage(
    canvas,
    clampedX,
    clampedY,
    clampedWidth,
    clampedHeight,
    0,
    0,
    smallWidth,
    smallHeight
  );

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(
    offscreenCanvas,
    0,
    0,
    smallWidth,
    smallHeight,
    clampedX,
    clampedY,
    clampedWidth,
    clampedHeight
  );
  ctx.imageSmoothingEnabled = true;
}

/**
 * Get canvas coordinates from a mouse event, accounting for scaling
 */
export function getCanvasCoordinates(
  e: React.MouseEvent | MouseEvent,
  canvas: HTMLCanvasElement
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY,
  };
}
